// @ts-nocheck
// Page shown when Lemon Squeezy redirects the buyer back after a successful
// checkout. The webhook (lemon-webhook Edge Function) writes the purchase row
// asynchronously, so we poll user_has_paid_access for up to ~15 seconds before
// either jumping the user into chapter 2 or showing a "still processing" hint.

import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/useAuth";
import { useAccess } from "../lib/useAccess";

const styles = `
.psucc-wrap { padding: 32px 16px; direction: rtl; min-height: 60vh; display: flex; align-items: center; justify-content: center; }
.psucc-card { background: linear-gradient(135deg, #FFF8F0 0%, #FFFDF9 100%); border: 1px solid #E8DCC8; border-radius: 24px; padding: 48px 32px; text-align: center; box-shadow: 0 12px 48px rgba(92,61,46,0.10); max-width: 520px; width: 100%; position: relative; overflow: hidden; }
.psucc-icon { font-size: 56px; margin-bottom: 16px; }
.psucc-title { font-family: 'Frank Ruhl Libre', serif; font-size: 28px; font-weight: 700; color: #5C3D2E; margin-bottom: 12px; line-height: 1.3; }
.psucc-lead { font-size: 16px; line-height: 1.7; color: #5C3D2E; margin-bottom: 24px; }
.psucc-spinner { width: 28px; height: 28px; border: 3px solid rgba(160,112,74,0.2); border-top-color: #A0704A; border-radius: 50%; animation: psucc-spin 0.9s linear infinite; margin: 0 auto 16px; }
@keyframes psucc-spin { to { transform: rotate(360deg); } }
.psucc-cta { background: linear-gradient(135deg, #A0704A, #D4875A); color: white; border: none; padding: 14px 32px; border-radius: 16px; font-family: 'Heebo', sans-serif; font-size: 16px; font-weight: 600; cursor: pointer; box-shadow: 0 6px 20px rgba(160,112,74,0.35); }
.psucc-fine { font-size: 13px; color: #8B7355; margin-top: 24px; line-height: 1.6; }
`;

const POLL_INTERVAL_MS = 1500;
const MAX_WAIT_MS = 20_000;

type Phase = "polling" | "ready" | "timeout" | "no_user";

export function PaymentSuccess() {
  const { user, loading: authLoading } = useAuth();
  const { hasAccess, refresh } = useAccess();
  const [phase, setPhase] = useState<Phase>("polling");
  const startedAtRef = useRef<number>(Date.now());

  // Poll until access is granted or we hit the timeout.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setPhase("no_user");
      return;
    }
    if (hasAccess) {
      setPhase("ready");
      return;
    }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await refresh();
      if (cancelled) return;
      const elapsed = Date.now() - startedAtRef.current;
      if (elapsed > MAX_WAIT_MS) {
        setPhase("timeout");
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, hasAccess, refresh]);

  // When access is granted, leave a small "you're in" flag for App.tsx to
  // surface a celebratory banner on chapter 2.
  useEffect(() => {
    if (phase === "ready") {
      try {
        window.sessionStorage.setItem("just_purchased", "1");
      } catch {}
    }
  }, [phase]);

  // Meta Pixel Purchase event — landing on this page means Lemon Squeezy
  // completed a checkout, so fire once per browser session regardless of
  // how fast the webhook grants access. Ads Manager uses this to report
  // purchases per campaign. fbq is loaded in index.html (skipped on
  // localhost); the sessionStorage guard stops double-counting when the
  // buyer refreshes the page while polling.
  useEffect(() => {
    try {
      const w = window as any;
      if (typeof w.fbq !== "function") return;
      if (window.sessionStorage.getItem("fb_purchase_sent") === "1") return;
      w.fbq("track", "Purchase", { currency: "ILS", value: 89 });
      window.sessionStorage.setItem("fb_purchase_sent", "1");
    } catch {}
  }, []);

  const goToChapter2 = () => {
    window.location.href = "/?chapter=2";
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="psucc-wrap">
        <div className="psucc-card">
          {phase === "polling" && (
            <>
              <div className="psucc-spinner" />
              <h1 className="psucc-title">מאשרים את הרכישה…</h1>
              <p className="psucc-lead">
                עוד רגע. אנחנו מסיימים לחבר את החשבון שלך לרישיון. זה לוקח עד כמה שניות.
              </p>
            </>
          )}

          {phase === "ready" && (
            <>
              <div className="psucc-icon">🎉</div>
              <h1 className="psucc-title">ברוכים הבאים לסדנא</h1>
              <p className="psucc-lead">
                הרכישה אושרה והגישה שלך פתוחה. אפשר להתחיל מפרק 2.
              </p>
              <button className="psucc-cta" onClick={goToChapter2}>
                להתחיל בפרק 2
              </button>
            </>
          )}

          {phase === "timeout" && (
            <>
              <div className="psucc-icon">⏳</div>
              <h1 className="psucc-title">הרכישה התקבלה</h1>
              <p className="psucc-lead">
                התשלום בוצע, אבל החיבור הסופי לחשבון מתעכב מעט. זה יקרה לבד תוך דקה.
                אם זה ממשיך —{" "}
                <a
                  href="mailto:shlomo@eiforall.com?subject=%D7%A8%D7%9B%D7%99%D7%A9%D7%94%20%D7%94%D7%AA%D7%A7%D7%91%D7%9C%D7%94%20%D7%90%D7%91%D7%9C%20%D7%90%D7%99%D7%9F%20%D7%92%D7%99%D7%A9%D7%94"
                  style={{ color: "#A0704A", fontWeight: 500 }}
                >
                  כתבו לנו
                </a>
                {" "}ונסדר ידנית.
              </p>
              <button className="psucc-cta" onClick={() => window.location.reload()}>
                לרענן ולנסות שוב
              </button>
            </>
          )}

          {phase === "no_user" && (
            <>
              <div className="psucc-icon">👋</div>
              <h1 className="psucc-title">תודה על הרכישה</h1>
              <p className="psucc-lead">
                אנחנו צריכים שתתחברו לאותו מייל שאיתו ביצעתם את הרכישה כדי לפתוח את הגישה.
              </p>
              <button className="psucc-cta" onClick={() => (window.location.href = "/")}>
                להתחברות
              </button>
            </>
          )}

          <p className="psucc-fine">
            <strong>קבלה ופרטי חיוב:</strong> Lemon Squeezy שולחים אוטומטית קבלה
            במייל מיד אחרי התשלום (כולל מע"מ מפורט). Lemon Squeezy פועלים כ‑Merchant
            of Record ומטפלים בעצמם בענייני המס וה‑VAT.
          </p>
          <p className="psucc-fine">
            צריכים <strong>חשבונית מס בעברית</strong> על שם עוסק/חברה?{" "}
            <a
              href="mailto:shlomo@eiforall.com?subject=%D7%91%D7%A7%D7%A9%D7%AA%20%D7%97%D7%A9%D7%91%D7%95%D7%A0%D7%99%D7%AA%20%D7%9E%D7%A1"
              style={{ color: "#A0704A", fontWeight: 500 }}
            >
              שלחו לנו מייל
            </a>
            {" "}עם מספר ההזמנה ושם העוסק/חברה — נשלח חשבונית בעברית תוך יום
            עסקים.
          </p>
          <p className="psucc-fine" style={{ marginTop: 8 }}>
            עזרה נוספת? <a href="/contact" style={{ color: "#A0704A", fontWeight: 500 }}>צרו קשר</a>
          </p>
        </div>
      </div>
    </>
  );
}
